import { FormattedMessage } from "react-intl";
import { type UnitShortFragment, UnitType } from "../../lib/graphql";
import { useUnitOptions } from "../../lib/hooks/basic/units";
import { DeepNamePath, Form, shouldUpdate } from "../form/Form";
import { SelectField, SelectFieldProps } from "../form/SelectField";

interface UnitSelectProps extends SelectFieldProps {
  unitTypes?: Array<UnitType>;
  abbrLabel?: boolean;
  addUnits?: boolean;
  onUnitChange?: (unit?: UnitShortFragment) => void;
}

/**
 * A reusable select component for unit selection with support for filtering by unit types
 * and custom formatting of unit labels.
 *
 * @example
 * // Basic usage
 * <UnitSelect />
 *
 * @example
 * // Filter by specific unit types
 * <UnitSelect unitTypes={[UnitType.Weight, UnitType.Volume]} />
 *
 * @example
 * // With change handler
 * <UnitSelect onUnitChange={(unit) => console.log('Selected unit:', unit)} />
 */
export function UnitSelect({
  unitTypes,
  abbrLabel = true,
  addUnits = false,
  onUnitChange,
  ...props
}: UnitSelectProps) {
  const { unitOptions, loadingUnitOptions } = useUnitOptions({
    types: unitTypes,
    abbrLabel,
  });

  return (
    <SelectField
      allowClear={false}
      showSearch={false}
      options={
        addUnits
          ? [
              {
                key: "",
                label: (
                  <FormattedMessage id="units.abbr" defaultMessage="unit" />
                ),
              },
              ...unitOptions,
            ]
          : unitOptions
      }
      defaultValue={addUnits ? "" : undefined}
      loading={loadingUnitOptions}
      onSelect={(val: any) => {
        if (onUnitChange) {
          const unit = unitOptions?.find((u) => u.key == val)?.unit;
          onUnitChange(
            unit || (addUnits ? ({ id: "" } as UnitShortFragment) : undefined)
          );
        }
      }}
      style={{ minWidth: "90px" }}
      {...props}
    />
  );
}

function UnitSelectFormItem({
  name,
  readonly,
  onUnitChange,
  ...props
}: {
  name: DeepNamePath;
  readonly?: boolean;
  onUnitChange?: (value: UnitShortFragment) => void;
} & UnitSelectProps) {
  return (
    <Form.Item noStyle shouldUpdate={shouldUpdate(name)}>
      {({ getFieldValue, setFields }) =>
        readonly ? (
          props.labelRenderer &&
          props.labelRenderer(getFieldValue(name.concat("abbr")), {} as any)
        ) : (
          <Form.Item compact name={name.concat("id")}>
            <UnitSelect
              id={name.concat("id").join("_")}
              onUnitChange={(value) => {
                setFields([{ name, value }]);
                if (onUnitChange) onUnitChange(value);
              }}
              {...props}
            />
          </Form.Item>
        )
      }
    </Form.Item>
  );
}

UnitSelect.FormItem = UnitSelectFormItem;
